/**
 * Theme Service
 *
 * Manages app themes with persistence.
 * Themes use CSS custom properties for instant switching.
 *
 * CSS Variable Mapping (matches design system):
 * - Backgrounds: --bg-void, --bg-deep, --bg-surface, --bg-elevated, --bg-hover, --bg-active
 * - Text: --text-primary, --text-secondary, --text-tertiary, --text-inverse
 * - Accent: --accent, --accent-dim, --accent-glow
 * - Secondary: --cyan, --cyan-dim, --cyan-glow
 * - Semantic: --income, --income-bg, --expense, --expense-bg, --transfer, --transfer-bg
 * - Border: --border-subtle, --border-default, --border-focus
 */

// ============================================================
// Theme Definitions
// ============================================================

export interface ThemeColors {
  // Backgrounds (dark to light)
  bgVoid: string;       // Darkest background
  bgDeep: string;       // Sidebar, nav backgrounds
  bgSurface: string;    // Card backgrounds
  bgElevated: string;   // Elevated elements
  bgHover: string;      // Hover state
  bgActive: string;     // Active/pressed state

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;

  // Primary Accent
  accent: string;
  accentDim: string;
  accentGlow: string;

  // Secondary Accent
  cyan: string;
  cyanDim: string;
  cyanGlow: string;

  // Semantic Colors
  income: string;
  incomeBg: string;
  expense: string;
  expenseBg: string;
  transfer: string;
  transferBg: string;
  warning: string;
  warningBg: string;

  // Borders
  borderSubtle: string;
  borderDefault: string;
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  isDark: boolean;
  colors: ThemeColors;
}

// ============================================================
// Theme Library
// ============================================================

export const themes: Theme[] = [
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    description: 'Default - serene night colors from VSCode theme',
    isDark: true,
    colors: {
      // CORRECT Tokyo Night colors from design/shared/themes.css
      bgVoid: '#1a1b26',
      bgDeep: '#16161e',
      bgSurface: '#1a1b26',
      bgElevated: '#1f2335',
      bgHover: '#292e42',
      bgActive: '#343b58',
      textPrimary: '#c0caf5',
      textSecondary: '#9aa5ce',
      textTertiary: '#565f89',
      accent: '#7aa2f7',
      accentDim: '#5d7ec9',
      accentGlow: '#7aa2f725',
      cyan: '#7dcfff',
      cyanDim: '#5fb4e3',
      cyanGlow: '#7dcfff20',
      income: '#9ece6a',
      incomeBg: '#9ece6a15',
      expense: '#f7768e',
      expenseBg: '#f7768e15',
      transfer: '#7dcfff',
      transferBg: '#7dcfff15',
      warning: '#e0af68',
      warningBg: '#e0af6815',
      borderSubtle: '#292e42',
      borderDefault: '#3b4261',
    },
  },
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    description: 'Deep blue with cyan accents',
    isDark: true,
    colors: {
      bgVoid: '#030810',
      bgDeep: '#061018',
      bgSurface: '#0a1620',
      bgElevated: '#0e1c28',
      bgHover: '#142430',
      bgActive: '#1a2c3a',
      textPrimary: '#e0f0ff',
      textSecondary: '#8090a8',
      textTertiary: '#506070',
      accent: '#00bcd4',
      accentDim: '#0097a7',
      accentGlow: '#00bcd425',
      cyan: '#26c6da',
      cyanDim: '#00acc1',
      cyanGlow: '#26c6da20',
      income: '#4caf50',
      incomeBg: '#4caf5012',
      expense: '#ff5252',
      expenseBg: '#ff525212',
      transfer: '#2196f3',
      transferBg: '#2196f312',
      warning: '#ffc107',
      warningBg: '#ffc10712',
      borderSubtle: '#102030',
      borderDefault: '#1a3040',
    },
  },
  {
    id: 'emerald-dark',
    name: 'Emerald Dark',
    description: 'Dark with green accents',
    isDark: true,
    colors: {
      bgVoid: '#030806',
      bgDeep: '#06100c',
      bgSurface: '#0a1812',
      bgElevated: '#0e201a',
      bgHover: '#142820',
      bgActive: '#1a3228',
      textPrimary: '#e0fff0',
      textSecondary: '#80a890',
      textTertiary: '#506858',
      accent: '#10b981',
      accentDim: '#059669',
      accentGlow: '#10b98125',
      cyan: '#14b8a6',
      cyanDim: '#0d9488',
      cyanGlow: '#14b8a620',
      income: '#22c55e',
      incomeBg: '#22c55e12',
      expense: '#ef4444',
      expenseBg: '#ef444412',
      transfer: '#3b82f6',
      transferBg: '#3b82f612',
      warning: '#f59e0b',
      warningBg: '#f59e0b12',
      borderSubtle: '#102818',
      borderDefault: '#1a3828',
    },
  },
  {
    id: 'rose-dark',
    name: 'Rose Dark',
    description: 'Dark with pink/rose accents',
    isDark: true,
    colors: {
      bgVoid: '#080305',
      bgDeep: '#100608',
      bgSurface: '#180a0e',
      bgElevated: '#200e14',
      bgHover: '#28141a',
      bgActive: '#321a22',
      textPrimary: '#fff0f4',
      textSecondary: '#a88090',
      textTertiary: '#685058',
      accent: '#f43f5e',
      accentDim: '#e11d48',
      accentGlow: '#f43f5e25',
      cyan: '#fb7185',
      cyanDim: '#f43f5e',
      cyanGlow: '#fb718520',
      income: '#4ade80',
      incomeBg: '#4ade8012',
      expense: '#f87171',
      expenseBg: '#f8717112',
      transfer: '#60a5fa',
      transferBg: '#60a5fa12',
      warning: '#fbbf24',
      warningBg: '#fbbf2412',
      borderSubtle: '#281418',
      borderDefault: '#381c22',
    },
  },
  {
    id: 'amber-dark',
    name: 'Amber Dark',
    description: 'Warm dark with amber accents',
    isDark: true,
    colors: {
      bgVoid: '#080603',
      bgDeep: '#100c06',
      bgSurface: '#18120a',
      bgElevated: '#20180e',
      bgHover: '#281e14',
      bgActive: '#32261a',
      textPrimary: '#fff8f0',
      textSecondary: '#a89880',
      textTertiary: '#686050',
      accent: '#f59e0b',
      accentDim: '#d97706',
      accentGlow: '#f59e0b25',
      cyan: '#fbbf24',
      cyanDim: '#f59e0b',
      cyanGlow: '#fbbf2420',
      income: '#84cc16',
      incomeBg: '#84cc1612',
      expense: '#f87171',
      expenseBg: '#f8717112',
      transfer: '#38bdf8',
      transferBg: '#38bdf812',
      warning: '#fb923c',
      warningBg: '#fb923c12',
      borderSubtle: '#281c10',
      borderDefault: '#382818',
    },
  },
  {
    id: 'ninja-stealth',
    name: 'Ninja Stealth',
    description: 'Ultra-dark with subtle accents',
    isDark: true,
    colors: {
      bgVoid: '#020202',
      bgDeep: '#050505',
      bgSurface: '#0a0a0a',
      bgElevated: '#0f0f0f',
      bgHover: '#151515',
      bgActive: '#1a1a1a',
      textPrimary: '#e0e0e0',
      textSecondary: '#707070',
      textTertiary: '#454545',
      accent: '#6366f1',
      accentDim: '#4f46e5',
      accentGlow: '#6366f120',
      cyan: '#818cf8',
      cyanDim: '#6366f1',
      cyanGlow: '#818cf820',
      income: '#22c55e',
      incomeBg: '#22c55e10',
      expense: '#ef4444',
      expenseBg: '#ef444410',
      transfer: '#3b82f6',
      transferBg: '#3b82f610',
      warning: '#eab308',
      warningBg: '#eab30810',
      borderSubtle: '#181818',
      borderDefault: '#252525',
    },
  },
  {
    id: 'ocean-depths',
    name: 'Ocean Depths',
    description: 'Deep sea blue-green',
    isDark: true,
    colors: {
      bgVoid: '#020808',
      bgDeep: '#041010',
      bgSurface: '#081818',
      bgElevated: '#0c2020',
      bgHover: '#102828',
      bgActive: '#143232',
      textPrimary: '#e0ffff',
      textSecondary: '#70a8a8',
      textTertiary: '#407878',
      accent: '#06b6d4',
      accentDim: '#0891b2',
      accentGlow: '#06b6d425',
      cyan: '#22d3ee',
      cyanDim: '#06b6d4',
      cyanGlow: '#22d3ee20',
      income: '#34d399',
      incomeBg: '#34d39912',
      expense: '#f87171',
      expenseBg: '#f8717112',
      transfer: '#60a5fa',
      transferBg: '#60a5fa12',
      warning: '#fcd34d',
      warningBg: '#fcd34d12',
      borderSubtle: '#0c2828',
      borderDefault: '#143838',
    },
  },
  {
    id: 'sunset-glow',
    name: 'Sunset Glow',
    description: 'Warm orange-red accents',
    isDark: true,
    colors: {
      bgVoid: '#080403',
      bgDeep: '#100806',
      bgSurface: '#180c0a',
      bgElevated: '#20100e',
      bgHover: '#281614',
      bgActive: '#321c1a',
      textPrimary: '#fff8f0',
      textSecondary: '#a88878',
      textTertiary: '#685848',
      accent: '#f97316',
      accentDim: '#ea580c',
      accentGlow: '#f9731625',
      cyan: '#fb923c',
      cyanDim: '#f97316',
      cyanGlow: '#fb923c20',
      income: '#4ade80',
      incomeBg: '#4ade8012',
      expense: '#f87171',
      expenseBg: '#f8717112',
      transfer: '#38bdf8',
      transferBg: '#38bdf812',
      warning: '#fcd34d',
      warningBg: '#fcd34d12',
      borderSubtle: '#281410',
      borderDefault: '#382018',
    },
  },
  {
    id: 'lavender-mist',
    name: 'Lavender Mist',
    description: 'Soft purple tones',
    isDark: true,
    colors: {
      bgVoid: '#060308',
      bgDeep: '#0c0610',
      bgSurface: '#120a18',
      bgElevated: '#180e20',
      bgHover: '#1e1428',
      bgActive: '#261a32',
      textPrimary: '#f8f0ff',
      textSecondary: '#9880a8',
      textTertiary: '#605068',
      accent: '#a855f7',
      accentDim: '#9333ea',
      accentGlow: '#a855f725',
      cyan: '#c084fc',
      cyanDim: '#a855f7',
      cyanGlow: '#c084fc20',
      income: '#4ade80',
      incomeBg: '#4ade8012',
      expense: '#fb7185',
      expenseBg: '#fb718512',
      transfer: '#60a5fa',
      transferBg: '#60a5fa12',
      warning: '#fbbf24',
      warningBg: '#fbbf2412',
      borderSubtle: '#1c1028',
      borderDefault: '#281838',
    },
  },
];

// ============================================================
// Theme Service
// ============================================================

const THEME_STORAGE_KEY = 'ninjacash-theme';
const FONT_SIZE_STORAGE_KEY = 'ninjacash-font-size';
const DEFAULT_THEME_ID = 'tokyo-night';
const DEFAULT_FONT_SIZE = 16;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;

// Font size presets for quick selection
export interface FontSizePreset {
  id: string;
  name: string;
  size: number;
}

export const fontSizePresets: FontSizePreset[] = [
  { id: 'compact', name: 'Compact', size: 14 },
  { id: 'default', name: 'Default', size: 16 },
  { id: 'large', name: 'Large', size: 18 },
  { id: 'extra-large', name: 'Extra Large', size: 20 },
];

/**
 * Get current theme ID from storage.
 */
export function getCurrentThemeId(): string {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

/**
 * Get theme by ID.
 */
export function getTheme(themeId: string): Theme {
  return themes.find((t) => t.id === themeId) || themes[0];
}

/**
 * Get current theme object.
 */
export function getCurrentTheme(): Theme {
  return getTheme(getCurrentThemeId());
}

/**
 * Apply theme to document (sets CSS variables).
 * Uses the design system variable names.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const { colors } = theme;

  // Backgrounds
  root.style.setProperty('--bg-void', colors.bgVoid);
  root.style.setProperty('--bg-deep', colors.bgDeep);
  root.style.setProperty('--bg-surface', colors.bgSurface);
  root.style.setProperty('--bg-elevated', colors.bgElevated);
  root.style.setProperty('--bg-hover', colors.bgHover);
  root.style.setProperty('--bg-active', colors.bgActive);

  // Text
  root.style.setProperty('--text-primary', colors.textPrimary);
  root.style.setProperty('--text-secondary', colors.textSecondary);
  root.style.setProperty('--text-tertiary', colors.textTertiary);

  // Primary Accent
  root.style.setProperty('--accent', colors.accent);
  root.style.setProperty('--accent-dim', colors.accentDim);
  root.style.setProperty('--accent-glow', colors.accentGlow);

  // Secondary Accent
  root.style.setProperty('--cyan', colors.cyan);
  root.style.setProperty('--cyan-dim', colors.cyanDim);
  root.style.setProperty('--cyan-glow', colors.cyanGlow);

  // Semantic Colors
  root.style.setProperty('--income', colors.income);
  root.style.setProperty('--income-bg', colors.incomeBg);
  root.style.setProperty('--expense', colors.expense);
  root.style.setProperty('--expense-bg', colors.expenseBg);
  root.style.setProperty('--transfer', colors.transfer);
  root.style.setProperty('--transfer-bg', colors.transferBg);
  root.style.setProperty('--warning', colors.warning);
  root.style.setProperty('--warning-bg', colors.warningBg);

  // Borders
  root.style.setProperty('--border-subtle', colors.borderSubtle);
  root.style.setProperty('--border-default', colors.borderDefault);

  // Store preference
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme.id);
  } catch {
    // Ignore storage errors
  }

  console.log('[Theme] Applied:', theme.name);
}

/**
 * Set theme by ID.
 */
export function setTheme(themeId: string): void {
  const theme = getTheme(themeId);
  applyTheme(theme);
}

/**
 * Initialize theme on app load.
 */
export function initTheme(): void {
  const theme = getCurrentTheme();
  applyTheme(theme);

  // Also apply font size
  const fontSize = getCurrentFontSize();
  applyFontSize(fontSize);
}

// ============================================================
// Font Size Service
// ============================================================

/**
 * Get current font size from storage.
 */
export function getCurrentFontSize(): number {
  try {
    const stored = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    if (stored) {
      const size = parseInt(stored, 10);
      if (!isNaN(size) && size >= MIN_FONT_SIZE && size <= MAX_FONT_SIZE) {
        return size;
      }
    }
  } catch {
    // Ignore storage errors
  }
  return DEFAULT_FONT_SIZE;
}

/**
 * Apply font size to document.
 * Sets the root font-size which cascades to all rem-based sizes.
 */
export function applyFontSize(size: number): void {
  // Clamp to valid range
  const clampedSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, size));

  // Set on document root
  document.documentElement.style.setProperty('--base-font-size', `${clampedSize}px`);
  document.documentElement.style.fontSize = `${clampedSize}px`;

  // Store preference
  try {
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, String(clampedSize));
  } catch {
    // Ignore storage errors
  }

  console.log('[Theme] Font size:', clampedSize + 'px');
}

/**
 * Set font size by pixel value.
 */
export function setFontSize(size: number): void {
  applyFontSize(size);
}

/**
 * Increment font size by 1 pixel.
 */
export function increaseFontSize(): number {
  const current = getCurrentFontSize();
  const newSize = Math.min(MAX_FONT_SIZE, current + 1);
  applyFontSize(newSize);
  return newSize;
}

/**
 * Decrement font size by 1 pixel.
 */
export function decreaseFontSize(): number {
  const current = getCurrentFontSize();
  const newSize = Math.max(MIN_FONT_SIZE, current - 1);
  applyFontSize(newSize);
  return newSize;
}

/**
 * Reset font size to default.
 */
export function resetFontSize(): number {
  applyFontSize(DEFAULT_FONT_SIZE);
  return DEFAULT_FONT_SIZE;
}

/**
 * Get the preset that matches current size, or null if custom.
 */
export function getCurrentFontSizePreset(): FontSizePreset | null {
  const size = getCurrentFontSize();
  return fontSizePresets.find((p) => p.size === size) || null;
}

/**
 * Font size limits for UI.
 */
export const fontSizeLimits = {
  min: MIN_FONT_SIZE,
  max: MAX_FONT_SIZE,
  default: DEFAULT_FONT_SIZE,
};
